import type { SessionItem } from './types';

// What a day looked like, derived from the sessions the app already loads.
// Pure and Date-free at the boundaries so the maths can be tested directly.

export type ProjectSlice = {
  projectId: number | null;
  projectName: string;
  color: string;
  seconds: number;
};

export type DayShape = {
  totalSeconds: number;
  /** Time per project, largest first. */
  projects: ProjectSlice[];
  /** Longest stretch of work with no gap longer than BLOCK_GAP_SECONDS. */
  longestBlockSeconds: number;
  sessionCount: number;
};

/** Two sessions closer than this read as one stretch of work, not two. */
export const BLOCK_GAP_SECONDS = 10 * 60;

const toMs = (iso: string): number =>
  new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').getTime();

const durationSeconds = (session: SessionItem): number => {
  if (typeof session.durationSeconds === 'number' && session.durationSeconds > 0) {
    return session.durationSeconds;
  }
  if (!session.endedAt) {
    return 0;
  }
  return Math.max(0, Math.round((toMs(session.endedAt) - toMs(session.startedAt)) / 1000));
};

export const dayShape = (
  sessions: SessionItem[],
  colorOf: (projectId: number | null) => string,
): DayShape => {
  const finished = sessions
    .filter((session) => Boolean(session.endedAt))
    .sort((a, b) => toMs(a.startedAt) - toMs(b.startedAt));

  const byProject = new Map<number | null, ProjectSlice>();
  let totalSeconds = 0;

  for (const session of finished) {
    const seconds = durationSeconds(session);
    totalSeconds += seconds;
    const key = session.projectId ?? null;
    const slice = byProject.get(key) ?? {
      projectId: key,
      projectName: session.projectName ?? 'Bez projektu',
      color: colorOf(key),
      seconds: 0,
    };
    slice.seconds += seconds;
    byProject.set(key, slice);
  }

  // Longest unbroken block: walk the sessions in order and keep merging while
  // the gap to the previous end stays small.
  let longestBlockSeconds = 0;
  let blockSeconds = 0;
  let blockEndMs = 0;
  for (const session of finished) {
    const startMs = toMs(session.startedAt);
    const seconds = durationSeconds(session);
    const gap = blockEndMs === 0 ? Infinity : (startMs - blockEndMs) / 1000;
    blockSeconds = gap <= BLOCK_GAP_SECONDS ? blockSeconds + seconds : seconds;
    blockEndMs = session.endedAt ? toMs(session.endedAt) : startMs + seconds * 1000;
    longestBlockSeconds = Math.max(longestBlockSeconds, blockSeconds);
  }

  return {
    totalSeconds,
    projects: [...byProject.values()].sort((a, b) => b.seconds - a.seconds),
    longestBlockSeconds,
    sessionCount: finished.length,
  };
};
